import React, { useRef, useState } from "react";
import { Play, Volume2, VolumeX, ArrowRight } from "lucide-react";

const SRC = "https://customer-assets-agu9un31.emergentagent.net/job_compete-edge-6/artifacts/3yqttmhx_16bac3f615e14df4bec76691a62c7b70_final.mp4";

export const PromoVideo = () => {
  const ref = useRef(null);
  const [started, setStarted] = useState(false);
  const [muted, setMuted] = useState(false);
  const [failed, setFailed] = useState(false);

  const play = async () => {
    const v = ref.current;
    if (!v) return;
    v.muted = muted;
    try { await v.play(); setStarted(true); }
    catch { v.muted = true; setMuted(true); await v.play(); setStarted(true); }
  };

  return (
    <section id="promo-video" className="noise relative overflow-hidden border-b border-white/10 bg-brand-dark" data-testid="promo-video-section">
      <div className="dot-grid absolute inset-0 opacity-50" />
      <div className="absolute -left-40 -top-40 h-[32rem] w-[32rem] rounded-full bg-klein/20 blur-[130px]" />
      <div className="absolute -right-24 bottom-0 h-[26rem] w-[26rem] rounded-full bg-cyan/10 blur-[120px]" />

      <div className="relative mx-auto grid max-w-6xl items-center gap-10 px-5 py-12 sm:py-16 lg:grid-cols-12 lg:px-8">
        <div className="lg:col-span-7">
          <p className="mono-label !text-cyan">Watch first · 2 min</p>
          <h2 className="mt-2 font-display text-3xl font-semibold leading-tight tracking-tight text-white sm:text-4xl">
            See what A.R.I.A. finds in a business like yours
          </h2>
          <p className="mt-4 max-w-lg text-base leading-relaxed text-slate-300">
            Two minutes on how A.R.I.A. scans a local business, scores the gaps competitors are winning on,
            and hands back a plan you can act on the same day.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button data-testid="promo-video-cta" onClick={play}
              className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-klein to-cyan px-6 py-3 text-sm font-semibold text-brand-dark transition-all hover:shadow-[0_0_28px_rgba(0,229,255,0.45)]">
              <Play size={15} fill="currentColor" /> Watch the 2-minute tour
              <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
            </button>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="group relative mx-auto w-full max-w-[20rem] overflow-hidden rounded-[1.75rem] border border-white/12 bg-black shadow-2xl glow-blue">
            <video
              ref={ref}
              data-testid="promo-video"
              src={SRC}
              preload="metadata"
              playsInline
              controls={started}
              onEnded={() => setStarted(false)}
              onError={() => setFailed(true)}
              className="aspect-[9/16] h-full w-full bg-black object-contain"
            />
            {failed && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-brand-dark/90 px-6 text-center" data-testid="promo-video-fallback">
                <p className="text-sm text-slate-300">This browser could not play the video inline.</p>
                <a href={SRC} target="_blank" rel="noreferrer"
                  className="rounded-full bg-gradient-to-r from-klein to-cyan px-5 py-2.5 text-sm font-semibold text-brand-dark">
                  Open the video
                </a>
              </div>
            )}
            {!started && !failed && (
              <button data-testid="promo-video-play" onClick={play}
                aria-label="Play the A.R.I.A. promotional video"
                className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-gradient-to-t from-brand-dark/80 via-brand-dark/15 to-transparent transition-colors hover:from-brand-dark/70">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-r from-klein to-cyan text-brand-dark shadow-[0_0_40px_rgba(0,229,255,0.45)] transition-transform duration-300 group-hover:scale-105">
                  <Play size={24} fill="currentColor" className="ml-0.5" />
                </span>
                <span className="mono-label !text-white/70">play with sound</span>
              </button>
            )}
            {started && (
              <button data-testid="promo-video-mute"
                onClick={() => { const v = ref.current; if (v) { v.muted = !v.muted; setMuted(v.muted); } }}
                className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full border border-white/20 bg-brand-dark/70 text-white backdrop-blur-sm transition-colors hover:bg-klein hover:text-brand-dark"
                aria-label={muted ? "Unmute" : "Mute"}>
                {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
